import React, { useState, useEffect } from "react";
import "./WebDevlopment.css";
import { Link, useNavigate } from "react-router-dom";

import { Button } from "antd";
import dataScienceImg from "../../../../utils/DataScience.webp";
import { ArrowRightOutlined } from "@ant-design/icons";
import {
  FaChartBar,
  FaCheckCircle,
  FaDatabase,
  FaSyncAlt,
  FaBrain,
  FaCog,
  FaChartLine,
  FaSearchDollar,
} from "react-icons/fa";

const DataScience = () => {
  const navigate = useNavigate();

  useEffect(() => {
    // Set page title and meta description
    document.title = "Data Science Services | Analytics & Machine Learning Solutions";
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Professional data science services including predictive analytics, data mining, statistical modeling, business intelligence, and machine learning solutions.');
    }
  }, []);
  const flipCards = [
    {
      title: "Predictive Analytics",
      description:
        "Build models that forecast future trends and behaviors using historical data patterns.",
      icon: <FaChartLine />,
    },
    {
      title: "Data Mining",
      description: "Extract valuable insights from large datasets to drive business decisions.",
      icon: <FaDatabase />,
    },
    {
      title: "Statistical Modeling",
      description:
        "Develop robust statistical models to understand relationships in your data.",
      icon: <FaChartBar />,
    },
    {
      title: "Business Intelligence",
      description:
        "Transform raw data into actionable business insights and visualizations.",
      icon: <FaSearchDollar />,
    },
    {
      title: "Data Engineering",
      description: "Design and build systems for collecting, storing and processing data.",
      icon: <FaCog />,
    },
    {
      title: "Machine Learning",
      description:
        "Implement algorithms that learn from data to make predictions and decisions.",
      icon: <FaBrain />,
    },
    {
      title: "Data Strategy",
      description:
        "Develop comprehensive plans to leverage data as a strategic asset.",
      icon: <FaChartBar />,
    },
  ];
  const accountabilityItems = [
    {
      number: "1",
      title: "Expertise",
      description:
        "Our team of 25+ data scientists brings deep expertise in statistics, machine learning, and data engineering to solve your most complex challenges.",
    },
    {
      number: "2",
      title: "Proven Methodology",
      description:
        "We follow a rigorous, iterative approach to data science that ensures reliable, actionable results you can trust.",
    },
    {
      number: "3",
      title: "Full-Cycle Solutions",
      description:
        "From data collection to model deployment, we handle the entire data science lifecycle for seamless integration.",
    },
    {
      number: "4",
      title: "Business-Focused",
      description:
        "We align data solutions with your specific business objectives to drive measurable impact and ROI.",
    },
    {
      number: "5",
      title: "Client Success",
      description:
        "Our data solutions have helped clients increase revenue by 15-40% and reduce costs by 20-35% through data-driven decisions.",
    },
    {
      number: "6",
      title: "Ethical Approach",
      description:
        "We prioritize data privacy, security, and ethical considerations in all our solutions.",
    },
  ];

  return (
    <>
      {/* Main Data Science Section */}
      <section className="web-dev-section">
        <div className="web-dev-container">
          <div className="web-dev-text-content">
            <h1>
              Unlock the Power of Your Data with Advanced Analytics Solutions
            </h1>
            <p>
              Transform raw data into strategic assets. Our data science solutions reveal hidden patterns, 
              predict future trends, and drive smarter business decisions.
            </p>
            <button
              className="web-dev-cta-button"
              onClick={() => navigate('/chaat')}
            >
              Let's discuss your data project
            </button>
            <div className="web-dev-features">
              <div className="web-dev-feature-item">📈 Predictive Analytics</div>
              <div className="web-dev-feature-item">🔍 Data Mining</div>
              <div className="web-dev-feature-item">📊 Statistical Modeling</div>
              <div className="web-dev-feature-item">💡 Business Intelligence</div>
              <div className="web-dev-feature-item">⚙️ Data Engineering</div>
              <div className="web-dev-feature-item">🤖 Machine Learning</div>
            </div>
          </div>
          <div className="web-dev-image-content">
            <img src={dataScienceImg} alt="Data Science Service Overview" />
          </div>
          
        </div>
      </section>

      {/* Overview Section */}
      <section className="web-dev-overview-section">
        <div className="web-dev-overview-container">
          {/* Left: Cards Section */}
          <div className="web-dev-cards-container">
            <div className="web-dev-card">
              <FaDatabase className="web-dev-card-icon" />
              <h3>Data-Driven Decisions</h3>
              <p>
                Move from gut feelings to evidence-based strategies with comprehensive data analysis.
              </p>
            </div>
            <div className="web-dev-card">
              <FaCheckCircle className="web-dev-card-icon" />
              <h3>Quality Assurance</h3>
              <p>
                Rigorous data validation and cleaning to ensure accuracy and reliability of insights.
              </p>
            </div>
            <div className="web-dev-card">
              <FaChartBar className="web-dev-card-icon" />
              <h3>Visual Storytelling</h3>
              <p>
                Transform complex data into clear, compelling visualizations that drive action.
              </p>
            </div>
            <div className="web-dev-card">
              <FaSyncAlt className="web-dev-card-icon" />
              <h3>Continuous Optimization</h3>
              <p>
                Regularly refine models and analyses as new data becomes available.
              </p>
            </div>
          </div>

          {/* Right: Overview Section */}
          <div className="web-dev-overview-content">
            <h2 className="web-dev-overview-title">
              Turning Data Into Competitive Advantage
            </h2>
            <p className="web-dev-overview-description">
              In today's digital economy, data is the new currency. Our data science services help 
              organizations extract maximum value from their information assets. We combine advanced 
              statistical techniques with cutting-edge machine learning to uncover patterns, predict 
              outcomes, and prescribe actions. Whether you need to understand customer behavior, 
              optimize operations, or identify new market opportunities, our team delivers solutions 
              tailored to your specific needs. We don't just build models - we create end-to-end data 
              products that integrate seamlessly with your business processes. From initial data 
              exploration to production deployment, we ensure your analytics initiatives deliver 
              tangible, measurable results. Our approach bridges the gap between technical sophistication 
              and business practicality, giving you the insights you need in formats you can use.
            </p>
          </div>
        </div>
      </section>

      {/* Flipping Cards Section */}
      <section className="web-dev-flipping-cards-section">
        <h2 className="flipping-cards-title">
          Ready to Transform Your Data Into Actionable Insights?
        </h2>
        <p className="flipping-cards-subtitle">
          Connect with our data science experts to explore how advanced analytics can solve your business challenges.
        </p>
        <div className="flipping-cards-container">
          {flipCards.map((card, index) => (
            <div className="flipping-card" key={index}>
              <div className="flipping-card-inner">
                <div className="flipping-card-front">
                  <div className="flipping-card-icon">{card.icon}</div>
                  <h3>{card.title}</h3>
                </div>
                <div className="flipping-card-back">
                  <p>{card.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>
      
      {/* Accountability Section */}
      <section className="accountability-section">
        <div className="accountability-header">
          <div className="accountability-header-left">
            <h1>We take absolute Accountability!</h1>
          </div>
          <div className="accountability-header-right">
            <p>
              Choose us for comprehensive data expertise, proven methodologies, and commitment 
              to delivering analytics solutions that create real business impact.
            </p>
            <Button
              className="custom-gradient-button"
              icon={<ArrowRightOutlined />}
              iconPosition="end"
              onClick={() => navigate('/chaat')}
            >
              Let's Chat
            </Button>
          </div>
        </div>
        <div className="accountability-grid">
          {accountabilityItems.map((item, index) => (
            <div key={index} className="accountability-item">
              <div className="accountability-number">{item.number}</div>
              <div className="accountability-content">
                <h3>{item.title}</h3>
                <p>{item.description}</p>
              </div>
            </div>
          ))}
        </div>
      </section>
    </>
  );
};

export default DataScience;